import { CalendarDays, ChevronLeft, ChevronRight } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

export interface MonthPickerProps {
  value: string;
  onChange: (value: string) => void;
  label?: string;
  disabled?: boolean;
}

export function MonthPicker({
  value,
  onChange,
  label,
  disabled,
}: MonthPickerProps) {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const selectedYear = Number(value.slice(0, 4)) || new Date().getFullYear();
  const [year, setYear] = useState(selectedYear);
  const month = Number(value.slice(5, 7)) - 1;
  const formatter = new Intl.DateTimeFormat(i18n.resolvedLanguage || "en", {
    month: "short",
  });
  const display =
    month >= 0 && month < 12
      ? new Intl.DateTimeFormat(i18n.resolvedLanguage || "en", {
          month: "long",
          year: "numeric",
        }).format(new Date(selectedYear, month, 1))
      : t("select_month");
  return (
    <Popover
      open={open}
      onOpenChange={(v) => {
        if (v) setYear(selectedYear);
        setOpen(v);
      }}
    >
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          disabled={disabled}
          aria-label={label || t("month")}
          className="w-full justify-between font-normal"
        >
          <span>{display}</span>
          <CalendarDays className="text-gray-500" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-72 space-y-3 p-3"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-center gap-2">
          <Button
            type="button"
            size="icon"
            variant="ghost"
            aria-label={t("previous_year")}
            disabled={year <= 1900}
            onClick={() => setYear(year - 1)}
          >
            <ChevronLeft />
          </Button>
          <Input
            aria-label={t("year")}
            type="number"
            min={1900}
            max={9999}
            value={year}
            onChange={(e) => {
              const n = Number(e.target.value);
              setYear(n);
            }}
            onBlur={() => {
              if (year < 1900 || year > 9999) setYear(selectedYear);
            }}
            className="text-center"
          />
          <Button
            type="button"
            size="icon"
            variant="ghost"
            aria-label={t("next_year")}
            disabled={year >= 9999}
            onClick={() => setYear(year + 1)}
          >
            <ChevronRight />
          </Button>
        </div>
        <div className="grid grid-cols-3 gap-1">
          {Array.from({ length: 12 }, (_, m) => (
            <Button
              key={m}
              disabled={year < 1900 || year > 9999}
              type="button"
              aria-pressed={selectedYear === year && month === m}
              variant={
                selectedYear === year && month === m ? "primary" : "ghost"
              }
              onClick={(event) => {
                event.preventDefault();
                onChange(`${year}-${String(m + 1).padStart(2, "0")}`);
                setOpen(false);
              }}
            >
              {formatter.format(new Date(2000, m, 1))}
            </Button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
